<template>
  <div class="HeroHome">
    <div class="hero-home__overlay"/>
    <video
      class="home__loop"
      loop
      playsinline
      autoplay
      muted
    >
      <source
        src="loop.mp4"
        type="video/mp4"
      >
    </video>
    <ModalBackdrop
      :id="modalId"
      :transition-props="{ name: 'modal__fade' }"
      mode="show"
      class="home__video-modal"
      @opened="play"
      @closed="pause"
    >
      <ModalBackdropDialog
        aria-label="video"
        class="home__video-container"
      >
        <div
          ref="video"
          class="home__video-wrapper"
        >
          <div class="home-video-close--wrapper">
            <ModalButton
              :controls="modalId"
              class="home__video-close"
            >
              <span class="access-hidden">Close Video</span>
              <CloseIcon class="home__close-icon"/>
            </ModalButton>
          </div>
          <iframe
            src="https://player.vimeo.com/video/330979639?loop=false&amp;byline=false&amp;portrait=false&amp;title=false&amp;speed=true&amp;transparent=0&amp;gesture=media"
            allowfullscreen
            allowtransparency
            allow="autoplay"
            class="home__video-video"
          />
        </div>
      </ModalBackdropDialog>
    </ModalBackdrop>
    <div class="home__heading-container">
      <span class="home__heading-top">All In.</span>
      <span class="home__heading-bottom">All Out.</span>
      <ModalButton
        :controls="modalId"
        class="home__modal-button"
      >
        <span class="access-hidden">Play Video</span>
        <PlayIcon class="home__play-icon"/>
      </ModalButton>
    </div>
  </div>
</template>

<script>
  import Plyr from 'plyr';
  import ModalButton from './ModalButton';
  import ModalBackdrop from './ModalBackdrop';
  import ModalBackdropDialog from './ModalBackdropDialog';
  import CloseIcon from './CloseIcon';
  import PlayIcon from './PlayIcon';

  export default {
    components: {
      ModalButton,
      ModalBackdrop,
      ModalBackdropDialog,
      CloseIcon,
      PlayIcon,
    },
    data() {
      return {
        video: undefined,
      };
    },
    computed: {
      modalId() {
        return `modal-${this._uid}`;
      },
    },
    mounted() {
      this.video = new Plyr(this.$refs.video);
    },
    methods: {
      play() {
        this.video.play();
      },
      pause() {
        this.video.pause();
      },
    },
  };
</script>

<style
  scoped
  lang="scss"
>
  .HeroHome {
    position: relative;
    overflow: hidden;
    width: 100vw;
    height: 250px;
    background-color: color(slate);

    @include breakpoint(phablet) {
      height: 100vh;
    }
  }

  .home__loop {
    position: relative;
    z-index: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .home__heading-container {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 11;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
  }

  .access-hidden {
    @include access-hidden;
  }

  .home__heading-top,
  .home__heading-bottom {
    font-family: font-family(sans-serif);
    font-weight: font-weight(extra-bold);
    letter-spacing: 3px;
    text-transform: uppercase;
    color: color(white);
  }

  .home__heading-top {
    font-size: 1.3rem;

    @include breakpoint(phablet) {
      font-size: 3.3rem;
    }
  }

  .home__heading-bottom {
    font-size: 2.8rem;

    @include breakpoint(phablet) {
      font-size: 7.3rem;
    }
  }

  .home__heading-top {
    order: 1;
  }

  .home__heading-bottom {
    order: 3;
  }

  .home__modal-button {
    display: flex;
    align-items: center;
    justify-content: center;
    order: 2;
    width: 40px;
    height: 40px;
    margin-top: spacing(base-gamma);
    margin-bottom: spacing(base-gamma);
    background-color: rgba(color(slate), 0.5);
    border-radius: 2px;
    outline: 0;
    transition-property: transform;
    transition-timing-function: timing-function(accelerate);
    transition-duration: duration(fast);

    &:hover,
    &:focus {
      transition-timing-function: timing-function(decelerate);
      transform: scale(0.9);
    }

    @include breakpoint(phablet) {
      width: 70px;
      height: 70px;
      margin-top: spacing(base-delta);
      margin-bottom: spacing(base-delta);
    }
  }

  .home__play-icon {
    fill: color(bronze);
    width: 10px;

    @include breakpoint(phablet) {
      width: 20px;
    }
  }

  .home-video-close--wrapper {
    width: 100%;
    display: flex;
    justify-content: flex-end;
  }

  .home__video-close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 35px;
    height: 35px;
    background-color: color(bronze);
    outline: 0;
    transition-property: background-color;
    transition-timing-function: timing-function(accelerate);
    transition-duration: duration(fast);

    &:hover,
    &:focus {
      transition-timing-function: timing-function(decelerate);
      background-color: darken(color(bronze), 10%);
    }
  }

  .home__close-icon {
    width: 25px;
    height: 25px;
    fill: color(white);
  }

  .home__video-modal {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 20;
    width: 100%;
    height: 100%;
    background-color: rgba(color(slate), 0.9);
  }

  .home__video-wrapper {
    position: relative;
    padding-bottom: 56.25%; /* 16:9 */
    padding-top: 25px;
    height: 0;
  }

  .home__video-video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .home__video-container {
    @include container;
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
  }

  .plyr--video {
    width: 100%;
  }

  .plyr--full-ui input[type='range'],
  .plyr__volume input[type='range'] {
    color: color(bronze)!important;
  }

  .plyr--video .plyr__controls .plyr__control.plyr__tab-focus,
  .plyr--video .plyr__controls .plyr__control:hover,
  .plyr--video .plyr__controls .plyr__control[aria-expanded='true'] {
    background-color: color(bronze)!important;
  }

  .hero-home__overlay {
    position: absolute;
    top: 0;
    left: 50%;
    width: 100%;
    height: 100%;
    z-index: 10;
    transform: skew(-45deg) translateX(-50%);
    background-color: rgba(color(slate), 0.5);
    box-shadow: 0px 0px 50px inset rgba(0, 0, 0, 0.25)
  }

  .modal__fade-enter-active {
    transition-property: opacity transform;
    transition-timing-function: timing-function(accelerate);
    transition-duration: duration(fast);
  }

  .modal__fade-leave-active {
    transition-property: opacity transform;
    transition-timing-function: timing-function(decelerate);
    transition-duration: duration(fast);
  }

  .modal__fade-enter,
  .modal__fade-leave-to {
    opacity: 0;
  }
</style>
